import { defineStore } from 'pinia'
import { getCurrentImportRun, getImportRun } from '@/api/v2/contactImports'
import type { ImportRun } from '@/models/ContactImport'

interface State {
  run: ImportRun | null
  loading: boolean
  timer: number | null
  inFlight: boolean
}

/**
 * The account's open import run. Shared by the Management "Fortschritt" tab and the wizard, so a
 * manager can close the wizard and still watch the job. Polling runs only in job phases.
 */
export const useContactImportStore = defineStore('contactImport', {
  state: (): State => ({ run: null, loading: false, timer: null, inFlight: false }),

  getters: {
    isJobPhase: (state): boolean => state.run !== null && (state.run.status === 'fetching' || state.run.status === 'applying'),
    polling: (state): boolean => state.timer !== null,
  },

  actions: {
    setRun(run: ImportRun | null) {
      this.run = run
    },

    async loadCurrent(): Promise<ImportRun | null> {
      this.loading = true
      try {
        const resp = await getCurrentImportRun()
        this.run = resp.status === 204 || !resp.data ? null : resp.data.data
      } finally {
        this.loading = false
      }
      return this.run
    },

    async load(runId: string): Promise<ImportRun> {
      const resp = await getImportRun(runId)
      this.run = resp.data.data
      return this.run
    },

    startPolling(intervalMs = 3000) {
      if (this.timer !== null) return
      const timer = window.setInterval(async () => {
        if (!this.run) return this.stopPolling()
        // Skip the tick while the previous request is still pending, so slow responses never overlap.
        if (this.inFlight) return
        const runId = this.run.id
        this.inFlight = true
        try {
          const resp = await getImportRun(runId)
          // Drop a late response once polling was stopped/restarted or another run was set meanwhile.
          if (this.timer !== timer || this.run?.id !== runId) return
          this.run = resp.data.data
        } catch {
          if (this.timer === timer) this.stopPolling()
          return
        } finally {
          this.inFlight = false
        }
        if (!this.isJobPhase) this.stopPolling()
      }, intervalMs)
      this.timer = timer
    },

    stopPolling() {
      if (this.timer !== null) {
        window.clearInterval(this.timer)
        this.timer = null
      }
    },
  },
})
